import { trigger, state, style, transition, animate } from "@angular/animations";
import { SelectionModel } from "@angular/cdk/collections";
import { Component, computed, input, OnDestroy, signal, TemplateRef, viewChild } from "@angular/core";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { Report } from "../../aixm-validator/models/Report";
import { Subject } from "rxjs";
export interface DetailedInfoContent {
  title: string;
  key: string;
  icon: string;
}

type GroupRow = { isGroup: true; id: string; group: string; items: Report[] };

@Component({
  selector: "app-validation-report-table",
  templateUrl: "./validation-report-table.component.html",
  styleUrls: ["./validation-report-table.component.scss"],
  animations: [
    trigger("detailExpand", [
      state("collapsed,void", style({ height: "0px", minHeight: "0" })),
      state("expanded", style({ height: "*" })),
      transition("expanded <=> collapsed", animate("225ms cubic-bezier(0.4, 0.0, 0.2, 1)")),
    ]),
    trigger("fadeInOut", [
      transition(":enter", [style({ opacity: 0 }), animate("200ms ease-in", style({ opacity: 1 }))]),
      transition(":leave", [animate("150ms ease-out", style({ opacity: 0 }))]),
    ]),
  ],
})
export class ValidationReportTableComponent implements OnDestroy {
  private destroy$ = new Subject<void>();

  expandedState = input<boolean>();

  reportListData = input.required<Report[]>();

  selectedGroupValue = input<string | null>();

  currentPageEvent = signal<PageEvent | null>(null);

  columnsConfig = [
    { columnDef: "indicator", header: "", cell: () => "" },
    { columnDef: "severity", header: "Severity", cell: (row: Report) => row.flag },
    { columnDef: "type", header: "Type", cell: (row: Report) => row.type },
    { columnDef: "schemaName", header: "Schema Name", cell: (row: Report) => row?.schemaName },
    { columnDef: "ruleId", header: "Rule ID", cell: (row: Report) => row.ruleId },
    { columnDef: "context", header: "Context", cell: (row: Report) => row.context },
    { columnDef: "diagnostic", header: "Diagnostic", cell: (row: Report) => row.errorMessage },
    { columnDef: "source", header: "Rule Source", cell: (row: Report) => row.source },
  ];

  detailedInfoContent: DetailedInfoContent[] = [
    { title: "Suggestion", key: "suggestion", icon: "lightbulb" },
    { title: "Location", key: "location", icon: "explore" },
    { title: "Rule", key: "rule", icon: "rule" },
    { title: "Schematron", key: "schematron", icon: "code" },
  ];

  displayedColumns = this.columnsConfig.map((c) => c.columnDef);

  customColumns = ["indicator", "ruleId", "severity"];

  source = computed(() => {
    const data = this.reportListData();
    const groupedMap = new Map<string, Report[]>();
    const groupedValue = this.selectedGroupValue();

    if (!data) {
      return new MatTableDataSource()
    }

    // If no grouping, return the data
    if (!groupedValue) {
      const tableSource = new MatTableDataSource(data);
      tableSource.paginator = this.paginator();
      return tableSource;
    }

    // Grouping
    data.forEach((item) => {
      const key = item[groupedValue] ?? "Unknown";
      if (!groupedMap.has(key)) groupedMap.set(key, []);
      groupedMap.get(key)!.push(item);
    });

    const groupedData: Array<Report | GroupRow> = [];

    for (const [group, items] of groupedMap.entries()) {
      const id = this.generateUUID();
      const itemsWithId = items.map((v) => ({ ...v, id }));
      const groupRow: GroupRow = { isGroup: true, id, group, items: itemsWithId };
      groupedData.push(groupRow);
      groupedData.push(...itemsWithId);
    }

    const tableSource = new MatTableDataSource(groupedData);
    tableSource.paginator = this.paginator();
    return tableSource;
  });

  rowExpandedState = computed(() => {
    const paginatedEvent = this.currentPageEvent();
    const sourceData = this.source().data;
    const pagedData = this.source()._pageData(sourceData);
    return new SelectionModel(true, this.expandedState() ? pagedData : []);
  });

  get rowExpanded() {
    return this.rowExpandedState();
  }

  groupExpandedState = computed(() => {
    const sourceData = this.source().data;
    const groupedRow = sourceData.filter((value: GroupRow) => value.isGroup).map((group: GroupRow) => group.id);
    return new SelectionModel<string>(true, this.expandedState() ? groupedRow : []);
  });

  get groupExpanded() {
    return this.groupExpandedState();
  }

  paginator = viewChild("paginator", { read: MatPaginator });

  indicator = viewChild("indicator", { read: TemplateRef });

  ruleId = viewChild("ruleId", { read: TemplateRef });

  locationTpl = viewChild("location", { read: TemplateRef });

  ruleTpl = viewChild("rule", { read: TemplateRef });

  schematronTpl = viewChild("schematron", { read: TemplateRef });

  severityTpl = viewChild("severity", { read: TemplateRef });

  private _customTplMap = computed(() => {
    return {
      indicator: this.indicator(),
      ruleId: this.ruleId(),
      location: this.locationTpl(),
      schematron: this.schematronTpl(),
      rule: this.ruleTpl(),
      severity: this.severityTpl(),
    };
  });

  get customTpl() {
    return this._customTplMap();
  }

  severityMap = {
    warn: { icon: "info", color: "accent" },
    warning: { icon: "info", color: "accent" },
    error: { icon: "error", color: "warn" },
    fatal: { icon: "cancel", color: null },
  };

  constructor() {}

  ngAfterViewInit(): void {}

  isGroupRow = (_: number, row: any): boolean => (row as any).isGroup === true;

  isNotGroup = (_: number, row: any) => !row.isGroup;

  setExpandedgroup = (element: GroupRow) => this.groupExpanded.toggle(element.id);

  checkExpanded = (row: Report | GroupRow): boolean => {
    const isGroupedEnabled = !!this.selectedGroupValue();
    const isRowExpanded = this.rowExpanded.isSelected(row);
    const isGroupRowExpanded = this.groupExpanded.isSelected((row as GroupRow).id);
    return isGroupedEnabled ? isGroupRowExpanded && isRowExpanded : isRowExpanded;
  };

  onPageChange = (event: PageEvent) => {
    this.currentPageEvent.set(event);
  };

  generateUUID(): string {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8; // UUID v4 variant
      return v.toString(16);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
